import { createFileRoute, redirect } from "@tanstack/react-router";
import Inventory from "@/pages/Inventory";
import { STATIC_ASSETS } from "@/lib/imageUtils";
import { modernHeroSources, homepageHeroDesktopPreload, normalizeHeroUrl } from "@/lib/heroPreload";
import { renderUrl } from "@/lib/imageUtils";

const TITLE = "RV Inventory | New & Used RVs for Sale | Rex & Sons RVs";
const DESCRIPTION =
  "Browse new and used RVs for sale near Wilmington, NC. Travel trailers, fifth wheels, motorhomes and more at Rex & Sons RVs.";

export const Route = createFileRoute("/rv-search/")({
  // Legacy/AI links like ?make=palomino&brand=puma fold into the keyword search.
  beforeLoad: ({ search }) => {
    const s = (search ?? {}) as Record<string, unknown>;
    // Legacy DealerSpike URLs also carry ?s=true&manufacturer=east+to+west.
    const keys = ["make", "brand", "model", "manufacturer", "mfr"] as const;
    if (!keys.some((k) => s[k] != null && s[k] !== "")) return;
    const rest: Record<string, unknown> = { ...s };
    for (const k of keys) delete rest[k];
    delete rest["s"];
    // Prefer the most specific term (model > brand > make) so "puma" isn't diluted by "palomino".
    const term = [s["model"], s["brand"], s["make"], s["manufacturer"], s["mfr"]].find((v) => v != null && String(v).trim() !== "");
    const q = [rest["q"], term].filter((v) => v != null && String(v).trim() !== "").join(" ");
    throw redirect({ to: "/rv-search", search: { ...rest, q } as never, replace: true });
  },
  // Filter/sort/page query strings all collapse onto the clean search URL.
  // Filtered views (?q=, ?type=, ...) share one description, so they are
  // marked noindex,follow — search engines index only the clean URL.
  head: ({ match, matches }) => {
    // This page uses fixed fallback slides, not CMS slides. Match the two
    // existing picture branches without adding a blocking banner query.
    const rootData = matches.find((item) => String(item.routeId) === "__root__")?.loaderData as { initialTheme?: { layout?: string } | null } | undefined;
    const modern = rootData?.initialTheme?.layout === "carmax";
    const sources = modernHeroSources(STATIC_ASSETS.heroInventory1920, STATIC_ASSETS.heroInventory640);
    const classic = homepageHeroDesktopPreload({ slides: [{ image_desktop: STATIC_ASSETS.heroInventory1920 }] });
    const mobile = modern ? sources.mobile : renderUrl(normalizeHeroUrl(STATIC_ASSETS.heroInventory640) || STATIC_ASSETS.heroInventory640, 750, 55);
    const desktop = modern ? sources.desktop : classic?.href;
    return {
    meta: [
      ...(Object.keys((match?.search as Record<string, unknown>) ?? {}).length
        ? [{ name: "robots", content: "noindex, follow" }]
        : []),
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.rexandsonsrvs.com/rv-search" },
      { name: "twitter:card", content: "summary_large_image" },
      ...(desktop?.startsWith("https://") ? [{ property: "og:image", content: desktop }, { name: "twitter:image", content: desktop }] : []),
    ],
    links: [
      { rel: "canonical", href: "https://www.rexandsonsrvs.com/rv-search" },
      { rel: "preload", as: "image", href: mobile, media: "(max-width: 767px)", fetchPriority: "high" },
      ...(desktop ? [{ rel: "preload", as: "image", href: desktop, media: "(min-width: 768px)", fetchPriority: "high" as const, imageSrcSet: modern ? undefined : classic?.imageSrcSet, imageSizes: modern ? undefined : classic?.imageSizes }] : []),
    ],
    };
  },
  component: Inventory,
});